'use client'

import React, { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { CheckCircle, Users, Target, Medal, TrendUp, Star, Lightning, Shield, Palette, Code, Eye, ChartBar, Lightbulb, ChatCircle, Heart, Gear, BookOpen } from 'phosphor-react'
import Components from '../components'

export default function Mentorship() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const achievements = [
    {
      icon: Shield,
      title: "WCAG 2.1 AA Compliance Expert",
      impact: "Delivered enterprise-grade accessibility audit across 95+ screens",
      level: "Professional Standard"
    },
    {
      icon: Users,
      title: "User Research Methodology",
      impact: "Led design thinking sessions and competitive analysis",
      level: "Strategic Thinking"
    },
    {
      icon: Palette,
      title: "Design System Architecture",
      impact: "Created comprehensive component documentation and motion guidelines",
      level: "Systems Design"
    },
    {
      icon: ChartBar,
      title: "Data-Driven UX Solutions",
      impact: "Resolved 50+ usability bugs with measurable business impact",
      level: "Business Impact"
    }
  ]

  const impactMetrics = [
    { number: "95+", label: "Screens Audited", description: "Comprehensive accessibility review" },
    { number: "50+", label: "Usability Bugs Closed", description: "Critical user experience issues resolved" },
    { number: "100%", label: "WCAG 2.1 AA", description: "Compliance standards met" },
    { number: "10+", label: "Design Projects Led", description: "End-to-end project leadership" }
  ]

  const skillAreas = [
    "Accessibility Compliance (WCAG 2.1)",
    "User Research & Design Thinking",
    "Design Systems Architecture",
    "Component Documentation & Libraries",
    "High-Fidelity Prototyping",
    "Interactive Prototype Development",
    "Design Thinking Facilitation",
    "Workshop Leadership & Ideation",
    "Responsive Design Auditing",
    "Motion Design Guidelines",
    "Competitive Analysis",
    "Problem Identification",
    "Solution Architecture",
    "Usability Testing & Validation",
    "UX Writing & Content Strategy"
  ]

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      <Components.GlassNavigation />
      
      <div className="pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-5xl font-bold text-[var(--brutal-ink)] mb-6 leading-tight">
              Design
              <span className="text-[var(--brutal-accent)]"> Mentorship</span>
            </h1>
            
            <p className="text-lg md:text-xl text-[var(--brutal-muted)] max-w-4xl mx-auto leading-relaxed">
              Outcomes from the people I've coached. I have provided guidance to designers who have accomplished the following:
            </p>
          </div>

          <Components.KeyDecisions
            decisions={[
              {
                question: 'Coach craft alone, or coach delivery under real constraints?',
                tension:
                  'Portfolio critiques raise the bar on visuals. They do not prepare designers for accessibility debt, stakeholder pushback, or shipping inside a sprint.',
                choice:
                  'I coach against live enterprise work so the skill is judged by what ships, not only what looks good in Figma.',
              },
              {
                question: 'Give answers, or make them own the diagnosis?',
                tension:
                  'Handing someone the fix is faster in the moment. It leaves them dependent the next time the same class of problem shows up.',
                choice:
                  'I push mentees to run the audit, name the tradeoff, and defend the call before we refine the solution together.',
              },
              {
                question: 'Broad skill coverage, or deep practice on high-leverage gaps?',
                tension:
                  'A long curriculum looks thorough. Most designers stall on a few gaps that block every project: research rigor, systems thinking, or accessibility.',
                choice:
                  'We pick the gaps that unblock their current work and drill those until the outcomes show up in production.',
              },
            ]}
          />

          {/* Impact Metrics */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4">Professional Achievements</h2>
            <p className="text-[var(--brutal-muted)]">Real results delivered by professionals I've coached</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {impactMetrics.map((metric, index) => (
              <div key={index} className="brutal-panel rounded-2xl p-6 text-center hover:bg-[var(--brutal-kraft)] transition-all duration-300">
                <div className="text-3xl font-bold text-[var(--brutal-ink)] mb-2">{metric.number}</div>
                <div className="text-[var(--brutal-ink)] font-semibold mb-1">{metric.label}</div>
                <div className="text-[var(--brutal-muted)] text-sm">{metric.description}</div>
              </div>
            ))}
          </div>

          {/* Achievement Showcase */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4 text-center">What They Deliver</h2>
            <p className="text-[var(--brutal-muted)] text-center mb-8">Enterprise-grade expertise developed through structured coaching</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {achievements.map((achievement, index) => (
                <div key={index} className="brutal-panel rounded-2xl p-4 md:p-8 hover:bg-[var(--brutal-kraft)] transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-r from-[var(--brutal-mustard)] to-[var(--brutal-mustard)] p-3 rounded-xl">
                      <achievement.icon weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                    </div>
                    <div className="flex-1">
                      <div className="mb-3">
                        <h3 className="text-lg md:text-xl font-bold text-[var(--brutal-ink)] mb-2">{achievement.title}</h3>
                        <Badge className="bg-[#8fce8f] text-[var(--brutal-ink)] border border-[var(--brutal-ink)] hover:bg-[#8fce8f] text-xs">
                          {achievement.level}
                        </Badge>
                      </div>
                      <p className="text-[var(--brutal-muted)] leading-relaxed">{achievement.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Skills Mastered */}
          <div className="mb-16">
            <Separator className="bg-[var(--brutal-panel)] mb-8" />
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4 text-center">Skills They Master</h2>
            <p className="text-sm md:text-base text-[var(--brutal-muted)] text-center mb-8">Comprehensive professional competencies developed through coaching</p>
            
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {skillAreas.map((skill, index) => (
                <div key={index} className="flex items-center gap-2 md:gap-3">
                  <CheckCircle weight="duotone" className="w-4 h-4 md:w-5 md:h-5 text-[var(--brutal-ink)] flex-shrink-0" />
                  <span className="text-[var(--brutal-ink)] font-medium text-sm md:text-base">{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Coaching Philosophy */}
          <div className="mb-16">
            <div className="brutal-panel p-8">
              <div className="text-center">
                <Lightning weight="duotone" className="w-12 h-12 text-[var(--brutal-ink)] mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-6">Coaching Philosophy</h2>
                <p className="text-lg md:text-xl text-[var(--brutal-muted)] max-w-4xl mx-auto leading-relaxed">
                  &ldquo;Great coaching isn&apos;t just about teaching tools. It&apos;s about guiding students in ways of thinking, so they can learn about their own strengths and weaknesses, and helping them develop a pathway for continuous growth.&rdquo;
                </p>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Clear, Mutual Goals */}
              <div className="brutal-panel rounded-2xl p-6 hover:bg-[var(--brutal-kraft)] transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-2 rounded-lg">
                    <Target weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--brutal-ink)]">Clear, Mutual Goals</h3>
                </div>
                <p className="text-[var(--brutal-muted)] mb-4">
                  Mentorship works best when both mentor and mentee set expectations: What skills, mindset, or projects are we targeting?
                </p>
                <div className="bg-[var(--brutal-kraft)] rounded-lg p-3 mb-4">
                  <p className="text-[var(--brutal-ink)] text-sm">
                    <strong>Example:</strong> Moving a designer from crafting UI to thinking systemically, or helping them lead research initiatives.
                  </p>
                </div>
                <div className="text-xs text-[var(--brutal-ink)]">
                  Source: UX Design Lab's Design Impact Framework (DIF) uses stages like Understand → Educate → Apply → Sustain.
                </div>
              </div>

              {/* Regular, Actionable Feedback */}
              <div className="brutal-panel rounded-2xl p-6 hover:bg-[var(--brutal-kraft)] transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-2 rounded-lg">
                    <ChatCircle weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--brutal-ink)]">Regular, Actionable Feedback</h3>
                </div>
                <p className="text-[var(--brutal-muted)] mb-4">
                  Feedback should be concrete, tied to real work. Not vague praise, but "Here's a thing that worked, here's something to try differently."
                </p>
                <div className="bg-[var(--brutal-kraft)] rounded-lg p-3">
                  <p className="text-[var(--brutal-ink)] text-sm">
                    Good UX mentorship programs place strong emphasis on this approach.
                  </p>
                </div>
                <div className="text-xs text-[var(--brutal-ink)] mt-3">
                  Source: UX Institute
                </div>
              </div>

              {/* Empower Learning, Not Just Teaching */}
              <div className="brutal-panel rounded-2xl p-6 hover:bg-[var(--brutal-kraft)] transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="brutal-btn p-2 rounded-lg">
                    <Lightbulb weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--brutal-ink)]">Empower Learning, Not Just Teaching</h3>
                </div>
                <p className="text-[var(--brutal-muted)] mb-4">
                  Rather than telling solutions, help mentees find their own through questioning, paired work, reviewing research/data.
                </p>
                <div className="bg-[var(--brutal-kraft)] rounded-lg p-3">
                  <p className="text-[var(--brutal-ink)] text-sm">
                    This builds confidence and long-term growth.
                  </p>
                </div>
              </div>

              {/* Create Safe Spaces for Experimentation */}
              <div className="brutal-panel rounded-2xl p-6 hover:bg-[var(--brutal-kraft)] transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 p-2 rounded-lg">
                    <Shield weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--brutal-ink)]">Create Safe Spaces for Experimentation</h3>
                </div>
                <p className="text-[var(--brutal-muted)] mb-4">
                  Allow for fail-fast, prototype, iterate. Make it okay to try things that might not work.
                </p>
                <div className="bg-[var(--brutal-kraft)] rounded-lg p-3">
                  <p className="text-[var(--brutal-ink)] text-sm">
                    Structure (timeline, check-ins, shared tools) helps reduce risk while enabling creative exploration.
                  </p>
                </div>
              </div>

              {/* Ground Mentorship in Real Impact */}
              <div className="brutal-panel rounded-2xl p-6 hover:bg-[var(--brutal-kraft)] transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-r from-[var(--brutal-mustard)] to-[var(--brutal-mustard)] p-2 rounded-lg">
                    <ChartBar weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--brutal-ink)]">Ground Mentorship in Real Impact</h3>
                </div>
                <p className="text-[var(--brutal-muted)] mb-4">
                  Link learnings to outcomes: user satisfaction, reduced errors, streamlined workflows, reduced rework, etc.
                </p>
                <div className="bg-[var(--brutal-kraft)] rounded-lg p-3">
                  <p className="text-[var(--brutal-ink)] text-sm">
                    Mentor designers not just to make prettier UIs but to influence product strategy, data, metrics.
                  </p>
                </div>
              </div>

              {/* Model Trust & Respect */}
              <div className="brutal-panel rounded-2xl p-6 hover:bg-[var(--brutal-kraft)] transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-r from-pink-500 to-rose-500 p-2 rounded-lg">
                    <Heart weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--brutal-ink)]">Model Trust & Respect</h3>
                </div>
                <p className="text-[var(--brutal-muted)] mb-4">
                  Be honest about your own mistakes, be transparent when you don&apos;t have answers.
                </p>
                <div className="bg-[var(--brutal-kraft)] rounded-lg p-3">
                  <p className="text-[var(--brutal-ink)] text-sm">
                    Respect the mentee's perspective; maintain psychological safety.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Image Modal */}
      {selectedImage && (
        <Components.ImageModal
          src={selectedImage}
          alt="Mentorship Achievement"
        />
      )}
    </div>
  )
}